/**
 * «Cambió el plano» (DWG/DXF): diff ENTIDAD POR ENTIDAD entre dos dibujos. Parte PURA y determinista ($0, sin IA).
 *
 * El diff por agregado (capa×planta, `cambioDePlanoDwg.ts`) dice CUÁNTO cambió; este dice DÓNDE: pinta lo nuevo, lo
 * quitado y lo que cambió, aunque el dibujo tenga una sola capa. Es solo para MOSTRAR: no entra a las cantidades ni a la
 * comparación guardada (esas siguen saliendo del inventario que valida el servidor).
 *
 * Lo que entra es una «firma» por entidad (la saca `apps/web/src/lib/firmasDxf.ts` del DXF ya leído):
 *  - geometría: tipo + puntos en METROS (una sola tolerancia, sin ángulos), con los extremos de una línea ordenados y las
 *    polilíneas cerradas normalizadas, para que dibujar al revés o empezar por otro vértice no cuente como cambio;
 *  - atributos: capa, texto, bloque y huella de su contenido. El color y el tipo de línea NO cuentan.
 * Los handles solo sirven DENTRO de un mismo archivo (para pintar): entre dos revisiones no son confiables.
 *
 * Los umbrales NO viven fijos aquí: son parámetros de Calibración (`cambio_movida_m`, `cambio_emparejar_distancia_m`,
 * `cambio_dwg_desplazado_pct`).
 */

export interface FirmaEntidad {
  /** Handle DENTRO de su archivo (para pintarla en el visor de ese archivo). */
  handle: string;
  /** Tipo canónico (LINE, LWPOLYLINE, ARC, CIRCLE, INSERT…). */
  tipo: string;
  /** Puntos de la geometría, en metros, ya normalizados (ver arriba). */
  puntos: readonly (readonly [number, number])[];
  /** Todo lo que no es geometría y cuenta como cambio, como «capa|texto|bloque|huella»: el PRIMER campo (antes del primer «|») es la capa. Igualdad exacta. */
  atributos: string;
}

export interface ParametrosDiffEntidades {
  /** m: por debajo, un punto no se movió (también es la rejilla del emparejado exacto). */
  movidaM: number;
  /** m: hasta aquí, una entidad del mismo tipo que se movió un poco es «la misma, movida». */
  emparejarM: number;
  /** %: si más que esto de AMBOS lados quedó sin pareja, el dibujo se movió/escaló entero: no se resalta pieza por pieza. */
  desplazadoPct: number;
}

export type RolDeEntidad = 'added' | 'deleted' | 'modified';

export interface ResultadoDiffEntidades {
  /** Handles de A con rol: quitadas (rojo) y modificadas (ámbar). */
  rolesA: Map<string, 'deleted' | 'modified'>;
  /** Handles de B con rol: nuevas (verde) y modificadas (ámbar). */
  rolesB: Map<string, 'added' | 'modified'>;
  /** Parejas A→B de handles (iguales y modificadas). */
  parejas: Map<string, string>;
  nuevas: number;
  quitadas: number;
  modificadas: number;
  iguales: number;
  /** El dibujo parece movido o a otra escala completo: no se puede marcar pieza por pieza. */
  desplazado: boolean;
}

const claveGeom = (f: FirmaEntidad, q: number): string => {
  let s = f.tipo;
  for (const [x, y] of f.puntos) s += `|${Math.round(x / q)},${Math.round(y / q)}`;
  return s;
};

const distMax = (a: FirmaEntidad, b: FirmaEntidad): number => {
  if (a.puntos.length !== b.puntos.length) return Infinity;
  let m = 0;
  for (let i = 0; i < a.puntos.length; i++) {
    const d = Math.hypot(a.puntos[i]![0] - b.puntos[i]![0], a.puntos[i]![1] - b.puntos[i]![1]);
    if (d > m) m = d;
  }
  return m;
};

const comparteUnPunto = (a: FirmaEntidad, b: FirmaEntidad, tol: number): boolean => {
  for (const p of a.puntos) for (const r of b.puntos) if (Math.hypot(p[0] - r[0], p[1] - r[1]) <= tol) return true;
  return false;
};

/**
 * Compara los dos dibujos. Tres pasadas, de lo más seguro a lo menos:
 *  1. exacta: misma geometría (a la tolerancia `movidaM`) → igual si también coinciden los atributos, modificada si no;
 *  2. por cercanía: mismo tipo y mismo número de puntos, a lo más `emparejarM` de distancia → igual (si cae dentro de la
 *     tolerancia: la frontera del redondeo) o modificada («se movió un poco»);
 *  3. por punto compartido: mismo tipo y mismo atributo de capa, compartiendo un punto → modificada (un vértice movido).
 * Lo que queda sin pareja: solo en A = quitada; solo en B = nueva. Los duplicados cuentan: 2 líneas iguales en A y 3 en B
 * dan 1 nueva.
 */
export function diffEntidades(a: readonly FirmaEntidad[], b: readonly FirmaEntidad[], p: ParametrosDiffEntidades): ResultadoDiffEntidades {
  const q = Math.max(p.movidaM, 1e-6);
  const rolesA = new Map<string, 'deleted' | 'modified'>();
  const rolesB = new Map<string, 'added' | 'modified'>();
  const parejas = new Map<string, string>();
  let iguales = 0;
  let modificadas = 0;

  const usadaA = new Set<number>();
  const usadaB = new Set<number>();
  const emparejar = (ia: number, ib: number, igual: boolean) => {
    usadaA.add(ia);
    usadaB.add(ib);
    parejas.set(a[ia]!.handle, b[ib]!.handle);
    if (igual) iguales++;
    else {
      modificadas++;
      rolesA.set(a[ia]!.handle, 'modified');
      rolesB.set(b[ib]!.handle, 'modified');
    }
  };

  // --- 1. exacta, por multiconjuntos ---
  const gruposB = new Map<string, number[]>();
  b.forEach((f, i) => {
    const k = claveGeom(f, q);
    const g = gruposB.get(k);
    if (g) g.push(i);
    else gruposB.set(k, [i]);
  });
  const gruposA = new Map<string, number[]>();
  a.forEach((f, i) => {
    const k = claveGeom(f, q);
    const g = gruposA.get(k);
    if (g) g.push(i);
    else gruposA.set(k, [i]);
  });
  for (const [k, ia] of gruposA) {
    const ib = gruposB.get(k);
    if (!ib) continue;
    // primero se parean los de atributos iguales (esos son «igual»); los que sobran se parean como «modificada»
    const restoA: number[] = [];
    const libresB = [...ib];
    for (const i of ia) {
      const j = libresB.findIndex((x) => b[x]!.atributos === a[i]!.atributos);
      if (j >= 0) {
        emparejar(i, libresB[j]!, true);
        libresB.splice(j, 1);
      } else restoA.push(i);
    }
    const n = Math.min(restoA.length, libresB.length);
    for (let t = 0; t < n; t++) emparejar(restoA[t]!, libresB[t]!, false);
  }

  // --- 2. por cercanía (rejilla espacial por el primer punto) ---
  const celda = Math.max(p.emparejarM, q);
  const cel = (x: number, y: number) => `${Math.floor(x / celda)},${Math.floor(y / celda)}`;
  const rejilla = new Map<string, number[]>();
  b.forEach((f, i) => {
    if (usadaB.has(i) || f.puntos.length === 0) return;
    const k = cel(f.puntos[0]![0], f.puntos[0]![1]);
    const g = rejilla.get(k);
    if (g) g.push(i);
    else rejilla.set(k, [i]);
  });
  a.forEach((fa, ia) => {
    if (usadaA.has(ia) || fa.puntos.length === 0) return;
    const cx = Math.floor(fa.puntos[0]![0] / celda);
    const cy = Math.floor(fa.puntos[0]![1] / celda);
    let mejor = -1;
    let mejorD = Infinity;
    for (let dx = -1; dx <= 1; dx++) {
      for (let dy = -1; dy <= 1; dy++) {
        for (const ib of rejilla.get(`${cx + dx},${cy + dy}`) ?? []) {
          if (usadaB.has(ib) || b[ib]!.tipo !== fa.tipo) continue;
          const d = distMax(fa, b[ib]!);
          if (d <= p.emparejarM && d < mejorD) {
            mejor = ib;
            mejorD = d;
          }
        }
      }
    }
    if (mejor >= 0) emparejar(ia, mejor, mejorD <= p.movidaM && fa.atributos === b[mejor]!.atributos);
  });

  // --- 3. por punto compartido (mismo tipo y capa): un vértice movido ---
  const restoB: number[] = [];
  b.forEach((_, i) => {
    if (!usadaB.has(i)) restoB.push(i);
  });
  a.forEach((fa, ia) => {
    if (usadaA.has(ia)) return;
    const capaA = fa.atributos.split('|')[0];
    const j = restoB.find((ib) => !usadaB.has(ib) && b[ib]!.tipo === fa.tipo && b[ib]!.atributos.split('|')[0] === capaA && comparteUnPunto(fa, b[ib]!, p.movidaM));
    if (j !== undefined) emparejar(ia, j, false);
  });

  // --- lo que quedó sin pareja ---
  let quitadas = 0;
  a.forEach((f, i) => {
    if (!usadaA.has(i)) {
      quitadas++;
      rolesA.set(f.handle, 'deleted');
    }
  });
  let nuevas = 0;
  b.forEach((f, i) => {
    if (!usadaB.has(i)) {
      nuevas++;
      rolesB.set(f.handle, 'added');
    }
  });

  // Guarda: si casi todo quedó sin pareja de los dos lados, el dibujo se movió o se reescaló entero. Se usa el MÍNIMO de las
  // dos proporciones para que agregar o quitar una planta entera no la dispare.
  const fracQuitadas = a.length > 0 ? quitadas / a.length : 0;
  const fracNuevas = b.length > 0 ? nuevas / b.length : 0;
  const desplazado = a.length > 0 && b.length > 0 && Math.min(fracQuitadas, fracNuevas) * 100 > p.desplazadoPct;

  return { rolesA, rolesB, parejas, nuevas, quitadas, modificadas, iguales, desplazado };
}
